import { useTranslation } from 'react-i18next';
import { SegmentedControl } from '@components/ui/SegmentedControl/SegmentedControl';
import type { ListItemKind } from '../model/listItem';
import type { useActivityFilters } from '../hooks/useActivityFilters';

/** The item-kind scope. `all` = no kind filter (the `types` array's "both
 *  selected" and "none selected" states are the same list). */
type KindScope = ListItemKind | 'all';

export interface IKindScopeTabsProps {
  f: ReturnType<typeof useActivityFilters>;
  /** The scope to show instead of the saved one — an alert drill-down shows
   *  the alert's kind for the visit. Choosing a segment still writes `f`. */
  types?: readonly ListItemKind[];
}

/**
 * All | Activities | Issues — the Daily List's kind scope. Sits directly
 * above the card list (inbox-tabs pattern: a switcher on top of a feed
 * narrows the feed) rather than inside the filter panel, whose checkbox
 * grammar it doesn't share. Drawn as the `joined` segmented group — the
 * filter-pill language (one outlined group, info tint on the active segment).
 */
export function KindScopeTabs({ f, types = f.filters.types }: IKindScopeTabsProps) {
  const { t } = useTranslation();
  const scope: KindScope = types.length === 1 ? types[0] ?? 'all' : 'all';
  return (
    <SegmentedControl<KindScope>
      variant="joined"
      value={scope}
      onChange={(next) => f.setTypes(next === 'all' ? [] : [next])}
      ariaLabel={t('activities.filter.itemType')}
      options={[
        { id: 'all', label: t('activities.filter.kindAll') },
        { id: 'activity', label: t('activities.filter.statusActivities') },
        { id: 'issue', label: t('activities.filter.statusIssues') },
      ]}
    />
  );
}
